import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useRazorpay, RazorpayOrderOptions } from 'react-razorpay';
import api from '../utils/api';
import { toast } from 'sonner';

function SeatSelection() {
  const { scheduleId } = useParams();
  const navigate = useNavigate();
  const { Razorpay } = useRazorpay();
  const [schedule, setSchedule] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    fetchSchedule();
  }, [scheduleId]);

  const fetchSchedule = async () => {
    try {
      const response = await api.get(`/schedules/${scheduleId}`);
      setSchedule(response.data);
    } catch (error) {
      toast.error('Failed to fetch schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSeatClick = (seat) => {
    if (selectedSeats.includes(seat)) {
      setSelectedSeats(selectedSeats.filter((s) => s !== seat));
      setPassengers(passengers.filter((p) => p.seat !== seat));
    } else {
      setSelectedSeats([...selectedSeats, seat]);
      setPassengers([...passengers, { seat, name: '', age: '', gender: '' }]);
    }
  };

  const updatePassenger = (seat, field, value) => {
    setPassengers(
      passengers.map((p) => (p.seat === seat ? { ...p, [field]: value } : p))
    );
  };

  const getSeatStatus = (seat) => {
    if (selectedSeats.includes(seat)) return 'selected';
    if (!schedule.available_seats.includes(seat)) return 'occupied';
    return 'available';
  };

  const getSeatColor = (status) => {
    if (status === 'selected') return 'bg-secondary border-secondary-600';
    if (status === 'occupied') return 'bg-slate-300 border-slate-400 cursor-not-allowed';
    return 'bg-white border-slate-300 hover:border-secondary hover:bg-secondary/10 cursor-pointer';
  };

  const handleBooking = async () => {
    if (selectedSeats.length === 0) {
      toast.error('Please select at least one seat');
      return;
    }

    const incomplete = passengers.find((p) => !p.name || !p.age || !p.gender);
    if (incomplete) {
      toast.error('Please fill all passenger details');
      return;
    }

    if (!contactEmail || !contactPhone) {
      toast.error('Please provide contact details');
      return;
    }

    setBooking(true);
    try {
      const totalAmount = schedule.price * selectedSeats.length;
      
      // Create booking
      const bookingResponse = await api.post('/bookings', {
        schedule_id: scheduleId,
        passengers: passengers.map((p) => ({
          name: p.name,
          age: parseInt(p.age),
          gender: p.gender,
          seat: p.seat,
        })),
        contact_email: contactEmail,
        contact_phone: contactPhone,
        total_amount: totalAmount,
      });

      const bookingId = bookingResponse.data.booking_id;

      // Create Razorpay order
      const orderResponse = await api.post('/payments/create-order', {
        booking_id: bookingId,
      });

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: 'Hasaka Tours & Travels',
        description: `Booking for ${selectedSeats.length} seat(s)`,
        order_id: orderResponse.data.order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await api.post('/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            toast.success('Payment successful!');
            navigate('/dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
            setBooking(false);
          }
        },
        prefill: {
          name: passengers[0]?.name || '',
          email: contactEmail,
          contact: contactPhone,
        },
        theme: {
          color: '#7A1A1A',
        },
        modal: {
          ondismiss: function () {
            setBooking(false);
            toast.error('Payment cancelled');
          },
        },
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-text-secondary font-public-sans">Loading schedule...</p>
        </div>
      </div>
    );
  }

  const allSeats = ['L1', 'R2', 'L2', 'R3', 'L3'];

  return (
    <div className="min-h-screen bg-neutral-background">
      <nav className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
              alt="Hasaka Tours & Travels"
              className="h-10 w-auto"
            />
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-primary font-medium text-sm uppercase tracking-wider"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h2 className="font-manrope font-bold text-2xl sm:text-3xl lg:text-4xl text-primary mb-6 sm:mb-8" data-testid="seat-selection-heading">
          Select Your Seats
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <div className="bg-white rounded-sm p-8 shadow-sm border border-slate-200">
            <h3 className="font-manrope font-bold text-2xl text-primary mb-6">BYD eMax 7 Seating</h3>
            <div className="space-y-6">
              <div className="bg-slate-100 rounded-sm p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-center">
                    <div className="border-2 border-slate-400 bg-slate-300 rounded-sm p-4 w-24 h-20 flex items-center justify-center">
                      <span className="font-mono font-bold text-slate-600">R1</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div
                      onClick={() => schedule.available_seats.includes('L1') && handleSeatClick('L1')}
                      className={`border-2 rounded-sm p-4 w-24 h-20 flex items-center justify-center transition-all ${getSeatColor(
                        getSeatStatus('L1')
                      )}`}
                      data-testid="seat-L1"
                    >
                      <span className="font-mono font-bold text-primary">L1</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div
                  onClick={() => schedule.available_seats.includes('R2') && handleSeatClick('R2')}
                  className={`border-2 rounded-sm p-4 h-20 flex items-center justify-center transition-all ${getSeatColor(
                    getSeatStatus('R2')
                  )}`}
                  data-testid="seat-R2"
                >
                  <span className="font-mono font-bold text-primary">R2</span>
                </div>
                <div
                  onClick={() => schedule.available_seats.includes('L2') && handleSeatClick('L2')}
                  className={`border-2 rounded-sm p-4 h-20 flex items-center justify-center transition-all ${getSeatColor(
                    getSeatStatus('L2')
                  )}`}
                  data-testid="seat-L2"
                >
                  <span className="font-mono font-bold text-primary">L2</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div
                  onClick={() => schedule.available_seats.includes('R3') && handleSeatClick('R3')}
                  className={`border-2 rounded-sm p-4 h-20 flex items-center justify-center transition-all ${getSeatColor(
                    getSeatStatus('R3')
                  )}`}
                  data-testid="seat-R3"
                >
                  <span className="font-mono font-bold text-primary">R3</span>
                </div>
                <div
                  onClick={() => schedule.available_seats.includes('L3') && handleSeatClick('L3')}
                  className={`border-2 rounded-sm p-4 h-20 flex items-center justify-center transition-all ${getSeatColor(
                    getSeatStatus('L3')
                  )}`}
                  data-testid="seat-L3"
                >
                  <span className="font-mono font-bold text-primary">L3</span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-white border-2 border-slate-300 rounded-sm"></div>
                <span className="font-public-sans text-slate-600">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-secondary border-2 border-secondary-600 rounded-sm"></div>
                <span className="font-public-sans text-slate-600">Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-slate-300 border-2 border-slate-400 rounded-sm"></div>
                <span className="font-public-sans text-slate-600">Occupied</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-sm p-8 shadow-sm border border-slate-200">
            <h3 className="font-manrope font-bold text-2xl text-primary mb-6">Passenger Details</h3>
            {selectedSeats.length === 0 ? (
              <p className="text-slate-500 font-public-sans">Please select seats to enter passenger details</p>
            ) : (
              <div className="space-y-6">
                {passengers.map((passenger) => (
                  <div key={passenger.seat} className="border border-slate-200 rounded-sm p-4">
                    <p className="font-mono font-bold text-secondary mb-3">Seat {passenger.seat}</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">
                          Name
                        </label>
                        <Input
                          value={passenger.name}
                          onChange={(e) => updatePassenger(passenger.seat, 'name', e.target.value)}
                          className="rounded-none border-slate-300 h-10"
                          placeholder="Full Name"
                          data-testid={`passenger-name-${passenger.seat}`}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">
                            Age
                          </label>
                          <Input
                            type="number"
                            value={passenger.age}
                            onChange={(e) => updatePassenger(passenger.seat, 'age', e.target.value)}
                            className="rounded-none border-slate-300 h-10"
                            placeholder="Age"
                            data-testid={`passenger-age-${passenger.seat}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">
                            Gender
                          </label>
                          <Select
                            onValueChange={(value) => updatePassenger(passenger.seat, 'gender', value)}
                            data-testid={`passenger-gender-${passenger.seat}`}
                          >
                            <SelectTrigger className="rounded-none border-slate-300 h-10">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Male">Male</SelectItem>
                              <SelectItem value="Female">Female</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h4 className="font-manrope font-bold text-lg text-primary mb-4">Contact Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        className="rounded-none border-slate-300 h-10"
                        placeholder="your.email@example.com"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">
                        Phone
                      </label>
                      <Input
                        type="tel"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        className="rounded-none border-slate-300 h-10"
                        placeholder="+91 1234567890"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-sm p-4 border border-slate-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-public-sans text-slate-600">Price per seat</span>
                    <span className="font-mono font-bold text-primary">₹{schedule.price}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-public-sans text-slate-600">Seats selected</span>
                    <span className="font-mono font-bold text-primary">{selectedSeats.length}</span>
                  </div>
                  <div className="border-t border-slate-300 mt-3 pt-3 flex justify-between items-center">
                    <span className="font-manrope font-bold text-lg text-primary">Total Amount</span>
                    <span className="font-mono font-bold text-3xl text-secondary">
                      ₹{(schedule.price * selectedSeats.length).toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleBooking}
                  disabled={booking}
                  className="bg-primary text-white hover:bg-primary-900 rounded-sm px-8 py-6 font-semibold tracking-wide uppercase text-sm w-full"
                  data-testid="proceed-to-payment-button"
                >
                  {booking ? 'Processing...' : 'Proceed to Payment'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SeatSelection;